import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import AdRotator from '../../components/adRotator/AdRotator';
import { AuthContext } from '../../context/AuthContext';
import {
  buildHitLabLoginPath,
  buildHitLabIntakeReturnTo,
  isHitLabClientEnabled,
} from './hitLabUtils';
import {
  EMPTY_RADIO_QUEUE,
  getActiveRadioQueue,
  getActiveRadioTracks,
  HITLAB_RADIO_ROUTE,
  mergeRadioCatalogItems,
  normalizeRadioCatalogResponseItems,
  RADIO_QUEUE_KINDS,
} from './hitLabRadioTracks';
import { HITLAB_FUNNEL_EVENTS, recordHitLabEvent } from './hitLabTelemetry';
import HitLabGalaxyGatePresence from './HitLabGalaxyGatePresence';
import HitLabProofSignalSection from './HitLabProofSignalSection';
import './hitLab.css';

const HITLAB_INSTAGRAM_URL = 'https://www.instagram.com/thejoegalaxy';

const HITLAB_MEDIA = {
  hero: {
    src:
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_880,h_1040/v1785821149/IMG_0088_lrcm1b.jpg',
    srcSet: [
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_520,h_680/v1785821149/IMG_0088_lrcm1b.jpg 520w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_880,h_1040/v1785821149/IMG_0088_lrcm1b.jpg 880w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_fill,g_auto,w_1160,h_1320/v1785821149/IMG_0088_lrcm1b.jpg 1160w',
    ].join(', '),
    sizes: '(max-width: 860px) calc(100vw - 32px), 38vw',
    alt: 'Joe Galaxy performing onstage at SunFest in front of a crowd.',
  },
  backstage: {
    src:
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_560/v1785821197/IMG_0209_rvufhz.jpg',
    srcSet: [
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_360/v1785821197/IMG_0209_rvufhz.jpg 360w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_560/v1785821197/IMG_0209_rvufhz.jpg 560w',
      'https://res.cloudinary.com/joegalaxy/image/upload/f_auto,q_auto,c_crop,g_north_west,w_4267,h_3200,x_80,y_380/c_scale,w_760/v1785821197/IMG_0209_rvufhz.jpg 760w',
    ].join(', '),
    sizes: '(max-width: 860px) calc(100vw - 32px), 26vw',
    alt: 'Joe Galaxy backstage at SunFest with Flo Rida after performing.',
  },
};

const formatPreviewTime = (value) => {
  const totalSeconds = Math.max(0, Math.floor(Number(value) || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export const HITLAB_LANDING_PREVIEW_NEXT_INDEX_KEY =
  'hitlab_radio_landing_preview_next_index';
export const HITLAB_LANDING_PREVIEW_ANON_SESSION_KEY =
  'hitlab_radio_preview_anon_session_id';

const RADIO_PREVIEW_PLAY_MIN_SECONDS = 30;
const RADIO_PREVIEW_PLAY_MIN_PERCENT = 25;
const HITLAB_LANDING_PREVIEW_SOURCE = 'hitlab_landing_radio_preview';
const HITLAB_SONG_AD_SURFACE_KEY = 'hitlab_landing';

let fallbackAnonymousPreviewSessionId = '';

const normalizePreviewIndex = (value, trackCount) => {
  if (trackCount <= 0) return 0;
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return 0;
  return ((parsed % trackCount) + trackCount) % trackCount;
};

const getBrowserLocalStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const createAnonymousPreviewSessionId = () =>
  [
    'hitlab-preview',
    Date.now(),
    Math.random().toString(36).slice(2, 12),
  ].join(':');

export const getLandingPreviewAnonymousSessionId = (
  storage = getBrowserLocalStorage(),
) => {
  if (!storage) {
    if (!fallbackAnonymousPreviewSessionId) {
      fallbackAnonymousPreviewSessionId = createAnonymousPreviewSessionId();
    }
    return fallbackAnonymousPreviewSessionId;
  }

  try {
    const existing = String(
      storage.getItem(HITLAB_LANDING_PREVIEW_ANON_SESSION_KEY) || '',
    ).trim();
    if (existing) return existing;
    const next = createAnonymousPreviewSessionId();
    storage.setItem(HITLAB_LANDING_PREVIEW_ANON_SESSION_KEY, next);
    return next;
  } catch {
    if (!fallbackAnonymousPreviewSessionId) {
      fallbackAnonymousPreviewSessionId = createAnonymousPreviewSessionId();
    }
    return fallbackAnonymousPreviewSessionId;
  }
};

export const getLandingPreviewStartIndex = (
  trackCount,
  storage = getBrowserLocalStorage(),
) => {
  if (trackCount <= 0 || !storage) return 0;
  try {
    const startIndex = normalizePreviewIndex(
      storage.getItem(HITLAB_LANDING_PREVIEW_NEXT_INDEX_KEY),
      trackCount,
    );
    storage.setItem(
      HITLAB_LANDING_PREVIEW_NEXT_INDEX_KEY,
      String((startIndex + 1) % trackCount),
    );
    return startIndex;
  } catch {
    return 0;
  }
};

const isLandingPreviewTrack = (item) => item?.kind === RADIO_QUEUE_KINDS.TRACK;
const isLandingPreviewBumper = (item) => item?.kind === RADIO_QUEUE_KINDS.BUMPER;
const isLandingPreviewStationProgramming = (item) =>
  item?.kind === RADIO_QUEUE_KINDS.BUMPER || item?.kind === RADIO_QUEUE_KINDS.DROP;

const findTrackQueueIndex = (queue, trackId) =>
  (Array.isArray(queue) ? queue : []).findIndex(
    (item) => isLandingPreviewTrack(item) && item.id === trackId,
  );

const findFirstTrackQueueIndex = (queue) =>
  (Array.isArray(queue) ? queue : []).findIndex(isLandingPreviewTrack);

const getLandingPreviewInitialQueueIndex = (queue = EMPTY_RADIO_QUEUE) => {
  const tracks = getActiveRadioTracks(queue);
  const activeQueue = getActiveRadioQueue(queue);
  const trackIndex = getLandingPreviewStartIndex(tracks.length);
  const queueIndex = findTrackQueueIndex(activeQueue, tracks[trackIndex]?.id);
  if (queueIndex >= 0) return queueIndex;
  return Math.max(0, findFirstTrackQueueIndex(activeQueue));
};

export const getNextLandingPreviewQueueIndex = (queue, currentIndex) => {
  const safeQueue = Array.isArray(queue) ? queue : [];
  const queueLength = safeQueue.length;
  if (queueLength <= 0) return 0;

  const safeCurrentIndex = normalizePreviewIndex(currentIndex, queueLength);
  const currentItem = safeQueue[safeCurrentIndex];
  const currentIsStationProgramming = isLandingPreviewStationProgramming(currentItem);

  for (let offset = 1; offset <= queueLength; offset += 1) {
    const candidateIndex = (safeCurrentIndex + offset) % queueLength;
    const candidate = safeQueue[candidateIndex];
    if (!candidate) continue;
    if (
      currentIsStationProgramming &&
      isLandingPreviewStationProgramming(candidate)
    ) {
      continue;
    }
    if (
      isLandingPreviewTrack(candidate) ||
      isLandingPreviewStationProgramming(candidate)
    ) {
      return candidateIndex;
    }
  }

  return Math.max(0, findFirstTrackQueueIndex(safeQueue));
};

const getLocalUtcDayKey = () => new Date().toISOString().slice(0, 10);

const buildLandingPreviewPlaySessionId = (trackId) =>
  [
    'hitlab-preview',
    String(trackId || 'track').replace(/[^a-z0-9_-]/gi, '').slice(0, 48),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ].join(':');

export default function HitLabLanding() {
  const { player } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const enabled = isHitLabClientEnabled();
  const previewAudioRef = useRef(null);
  const continuePreviewAfterAdvanceRef = useRef(false);
  const previewPlaySessionIdRef = useRef('');
  const anonymousPreviewPostKeysRef = useRef(new Set());
  const [previewQueueIndex, setPreviewQueueIndex] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [previewElapsed, setPreviewElapsed] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(0);
  const [radioQueue, setRadioQueue] = useState(EMPTY_RADIO_QUEUE);

  useEffect(() => {
    if (!enabled) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.LANDING_VIEWED, {
      search: location.search,
      path: '/hitlab',
    });
  }, [enabled, location.search]);

  useEffect(() => {
    let active = true;
    const loadRadioCatalog = async () => {
      try {
        const response = await axiosConn.get('hitlab/radio/catalog');
        if (!active) return;
        const items = normalizeRadioCatalogResponseItems(response);
        const nextQueue = mergeRadioCatalogItems(items);
        setRadioQueue(nextQueue);
        setPreviewQueueIndex(getLandingPreviewInitialQueueIndex(nextQueue));
      } catch {
        if (active) {
          setRadioQueue((current) =>
            current && current.length ? current : EMPTY_RADIO_QUEUE,
          );
        }
      }
    };

    void loadRadioCatalog();
    return () => {
      active = false;
    };
  }, []);

  const ctaLabel = enabled
    ? 'Put My Record In The Lab'
    : 'Artist intake opens soon';
  const activeRadioQueue = useMemo(
    () => getActiveRadioQueue(radioQueue),
    [radioQueue],
  );
  const activeRadioTracks = useMemo(
    () => getActiveRadioTracks(radioQueue),
    [radioQueue],
  );
  const activeRadioTrackCount = activeRadioTracks.length;
  const safePreviewQueueIndex = normalizePreviewIndex(
    previewQueueIndex,
    activeRadioQueue.length,
  );
  const featuredPreviewItem = activeRadioQueue[safePreviewQueueIndex] || null;
  const previewItemIsTrack = isLandingPreviewTrack(featuredPreviewItem);
  const previewItemIsBumper = isLandingPreviewBumper(featuredPreviewItem);
  const previewItemIsDrop = featuredPreviewItem?.kind === RADIO_QUEUE_KINDS.DROP;
  const previewItemIsStationProgramming =
    previewItemIsBumper || previewItemIsDrop;
  const radioLive = activeRadioTrackCount > 0;
  const previewTitle = previewItemIsTrack
    ? featuredPreviewItem.songTitle
    : featuredPreviewItem?.label ||
      featuredPreviewItem?.title ||
      (previewItemIsDrop ? 'Listener drop' : 'Station sweep');
  const previewSubtitle = previewItemIsTrack
    ? featuredPreviewItem.artistName
    : featuredPreviewItem?.title || 'Joe Galaxy Radio';

  useEffect(() => {
    if (!activeRadioQueue.length) return;
    setPreviewQueueIndex((current) => {
      const safeIndex = normalizePreviewIndex(current, activeRadioQueue.length);
      if (activeRadioQueue[safeIndex]) return safeIndex;
      return Math.max(0, findFirstTrackQueueIndex(activeRadioQueue));
    });
  }, [activeRadioQueue]);

  const pausePreviewAudio = useCallback(({ reset = false } = {}) => {
    continuePreviewAfterAdvanceRef.current = false;
    const audio = previewAudioRef.current;
    if (audio) {
      audio.pause();
      if (reset) {
        try {
          audio.currentTime = 0;
        } catch {
          // Some media shims expose currentTime as read-only.
        }
      }
    }
    setPreviewPlaying(false);
    setPreviewLoading(false);
    if (reset) setPreviewElapsed(0);
  }, []);

  useEffect(
    () => () => {
      const audio = previewAudioRef.current;
      if (audio) audio.pause();
    },
    [],
  );

  const startIntake = () => {
    pausePreviewAudio();
    if (!enabled) return;
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.START_ARTIST_INTAKE_CLICKED, {
      search: location.search,
      path: '/hitlab',
      dedupe: false,
    });
    if (player?.token) {
      history.push(buildHitLabIntakeReturnTo(location.search));
      return;
    }
    void recordHitLabEvent(HITLAB_FUNNEL_EVENTS.AUTH_STARTED, {
      search: location.search,
      path: '/hitlab',
      dedupe: false,
    });
    history.push(buildHitLabLoginPath(location.search));
  };

  const openFullRadio = () => {
    pausePreviewAudio();
    history.push(HITLAB_RADIO_ROUTE);
  };

  const toggleRadioPreview = async () => {
    if (!radioLive || !featuredPreviewItem) return;
    if (previewPlaying) {
      pausePreviewAudio();
      return;
    }

    const audio = previewAudioRef.current;
    if (!audio) return;

    void recordHitLabEvent(
      HITLAB_FUNNEL_EVENTS.LANDING_RADIO_PREVIEW_PLAY_CLICKED,
      {
        search: location.search,
        path: '/hitlab',
        dedupe: false,
      },
    );

    setPreviewError('');
    setPreviewLoading(true);
    try {
      await audio.play();
      setPreviewPlaying(true);
    } catch {
      setPreviewError('Preview audio is unavailable right now.');
      setPreviewPlaying(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewLoadedMetadata = () => {
    const nextDuration = Number(previewAudioRef.current?.duration);
    setPreviewDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
  };

  const recordAnonymousPreviewPlayIfReady = useCallback(
    async ({ nextElapsed, nextDuration }) => {
      if (!previewItemIsTrack || !featuredPreviewItem?.id) return;

      const listenedSeconds = Math.floor(Math.max(0, Number(nextElapsed) || 0));
      const listenPercent =
        nextDuration > 0
          ? Math.min(100, Math.round((listenedSeconds / nextDuration) * 100))
          : 0;

      if (
        listenedSeconds < RADIO_PREVIEW_PLAY_MIN_SECONDS &&
        listenPercent < RADIO_PREVIEW_PLAY_MIN_PERCENT
      ) {
        return;
      }

      const anonymousSessionId = getLandingPreviewAnonymousSessionId();
      if (!anonymousSessionId) return;

      const localDayKey = getLocalUtcDayKey();
      const postKey = `${anonymousSessionId}:${featuredPreviewItem.id}:${localDayKey}`;
      if (anonymousPreviewPostKeysRef.current.has(postKey)) return;
      anonymousPreviewPostKeysRef.current.add(postKey);
      if (!previewPlaySessionIdRef.current) {
        previewPlaySessionIdRef.current = buildLandingPreviewPlaySessionId(
          featuredPreviewItem.id,
        );
      }

      try {
        await axiosConn.post('hitlab/radio/play/anonymous', {
          trackId: featuredPreviewItem.id,
          listenSession: {
            listenedSeconds,
            listenPercent,
          },
          anonymousSessionId,
          clientPlaySessionId: previewPlaySessionIdRef.current,
          clientMeta: {
            source: HITLAB_LANDING_PREVIEW_SOURCE,
            route: '/hitlab',
          },
        });
      } catch {
        // Public preview play telemetry must never interrupt preview playback.
      }
    },
    [featuredPreviewItem, previewItemIsTrack],
  );

  const handlePreviewTimeUpdate = () => {
    const nextElapsed = Number(previewAudioRef.current?.currentTime);
    const safeElapsed = Number.isFinite(nextElapsed) ? nextElapsed : 0;
    const nextDuration = Number(previewAudioRef.current?.duration);
    const safeDuration = Number.isFinite(nextDuration)
      ? nextDuration
      : previewDuration;
    setPreviewElapsed(safeElapsed);
    if (previewPlaying) {
      void recordAnonymousPreviewPlayIfReady({
        nextElapsed: safeElapsed,
        nextDuration: safeDuration,
      });
    }
  };

  const handlePreviewEnded = () => {
    const endedElapsed = Number(previewAudioRef.current?.currentTime);
    const endedDuration = Number(previewAudioRef.current?.duration);
    void recordAnonymousPreviewPlayIfReady({
      nextElapsed: Number.isFinite(endedElapsed) ? endedElapsed : previewElapsed,
      nextDuration: Number.isFinite(endedDuration) ? endedDuration : previewDuration,
    });

    if (!activeRadioQueue.length) {
      pausePreviewAudio({ reset: true });
      return;
    }

    if (activeRadioQueue.length === 1) {
      const audio = previewAudioRef.current;
      if (!audio) {
        pausePreviewAudio({ reset: true });
        return;
      }

      setPreviewPlaying(false);
      setPreviewLoading(true);
      setPreviewElapsed(0);
      setPreviewDuration(0);
      setPreviewError('');
      try {
        audio.currentTime = 0;
      } catch {
        // Some media shims expose currentTime as read-only.
      }
      Promise.resolve(audio.play())
        .then(() => {
          setPreviewPlaying(true);
        })
        .catch(() => {
          setPreviewPlaying(false);
          setPreviewError('Preview paused. Tap Play Preview to continue.');
        })
        .finally(() => {
          setPreviewLoading(false);
        });
      return;
    }

    setPreviewPlaying(false);
    setPreviewLoading(false);
    setPreviewElapsed(0);
    setPreviewDuration(0);
    setPreviewError('');
    continuePreviewAfterAdvanceRef.current = true;
    setPreviewQueueIndex((current) =>
      getNextLandingPreviewQueueIndex(activeRadioQueue, current),
    );
  };

  const handlePreviewError = () => {
    setPreviewError('Preview audio is unavailable right now.');
    pausePreviewAudio();
  };

  useEffect(() => {
    previewPlaySessionIdRef.current = buildLandingPreviewPlaySessionId(
      featuredPreviewItem?.id,
    );
    setPreviewElapsed(0);
    setPreviewDuration(0);
    setPreviewError('');

    if (!continuePreviewAfterAdvanceRef.current) return;
    continuePreviewAfterAdvanceRef.current = false;

    const audio = previewAudioRef.current;
    if (!audio) return;

    setPreviewLoading(true);
    Promise.resolve(audio.play())
      .then(() => {
        setPreviewPlaying(true);
      })
      .catch(() => {
        setPreviewPlaying(false);
        setPreviewError('Preview paused. Tap Play Preview to continue.');
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  }, [featuredPreviewItem?.id]);

  const previewProgress =
    previewDuration > 0
      ? Math.min(100, Math.max(0, (previewElapsed / previewDuration) * 100))
      : 0;

  return (
    <main className="hitlab-page hitlab-landing">
      <Helmet>
        <title>JG HitLab Artist Intake | Joe Galaxy</title>
        <meta
          name="description"
          content="Submit one original record for the Joe Galaxy curated artist intake."
        />
      </Helmet>

      <section className="hitlab-hero">
        <div className="hitlab-shell hitlab-hero-grid">
          <div className="hitlab-hero-copy">
            <p className="hitlab-kicker">JG HITLAB ARTIST INTAKE</p>
            <h1>This is bigger than a song submission.</h1>
            <p className="hitlab-pattern">
              Joe Galaxy and the #DopeGalaxy movement are building a new
              independent music lane - where real artists, real records, real
              listeners, and real opportunity meet in the same universe.
            </p>
            <p className="hitlab-lede">
              If you believe you have a radio-ready record, JG HitLab is the door.
            </p>
            <p>
              One song. One shot. Real potential.
            </p>
            <div className="hitlab-cta-row">
              <button
                type="button"
                className="hitlab-primary-btn"
                onClick={startIntake}
                disabled={!enabled}
              >
                {ctaLabel}
              </button>
              <a className="hitlab-secondary-link" href="#hitlab-qualifies">
                See What Qualifies
              </a>
            </div>
            <ul className="hitlab-trust-chips" aria-label="HitLab intake boundaries">
              <li>Original music only</li>
              <li>Rights must be clear</li>
              <li>Placement cannot be bought</li>
              {radioLive ? <li>Radio Rotation is live</li> : null}
            </ul>
            <article className="hitlab-press-strip" aria-label="Press credibility">
              <p className="hitlab-kicker">PRESS CREDIBILITY</p>
              <p>Joe Galaxy music has been featured in The Source.</p>
              <a
                href="https://thesource.com/2017/11/30/joe-galaxy-drops-new-visual-drinkin-night-away/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open The Source article about Joe Galaxy in a new tab"
              >
                Joe Galaxy Drops New Visual For "Drinkin The Night Away" -&gt;
              </a>
            </article>
            <HitLabGalaxyGatePresence />
          </div>
          <aside
            className="hitlab-hero-card hitlab-hero-media-card"
            aria-label="Joe Galaxy performance credibility"
          >
            <figure className="hitlab-hero-media">
              <div className="hitlab-hero-media-frame">
                <img
                  src={HITLAB_MEDIA.hero.src}
                  srcSet={HITLAB_MEDIA.hero.srcSet}
                  sizes={HITLAB_MEDIA.hero.sizes}
                  alt={HITLAB_MEDIA.hero.alt}
                  width="880"
                  height="1040"
                  decoding="async"
                />
              </div>
              <figcaption>
                <span>Performed on the Ford Stage at SunFest</span>
                <h2>Real stage history. Serious curation.</h2>
                <small>
                  One song enters the review queue. Quality, rights clarity,
                  and curator decision set the next step.
                </small>
              </figcaption>
            </figure>
          </aside>
        </div>
      </section>

      <HitLabProofSignalSection
        enabled={enabled}
        onStartIntake={startIntake}
        ctaLabel={ctaLabel}
      />

      <section
        className="hitlab-shell hitlab-song-ad-section"
        aria-label="JG Universe Song Ad Rotation"
      >
        <div className="hitlab-song-ad-section-header">
          <div>
            <p className="hitlab-kicker">JG UNIVERSE SONG ADS</p>
            <h2>Records moving through JG Universe.</h2>
            <p>
              Cover-art ads can point listeners toward records that are already
              in Radio Rotation. HitLab Plus earns ad rotation eligibility;
              Radio Rotation still has to be earned.
            </p>
          </div>
        </div>
        <AdRotator
          surfaceKey={HITLAB_SONG_AD_SURFACE_KEY}
          mode="hitlab-song"
          hitLabSongAdOpenInNewTab
          renderEmpty={() => (
            <div className="hitlab-song-ad-empty">
              <strong>Song Ad Rotation is opening.</strong>
              <p>
                HitLab Plus records can appear here after song submission and
                rights review.
              </p>
              <button
                type="button"
                className="hitlab-secondary-btn hitlab-song-ad-empty-cta"
                onClick={startIntake}
                disabled={!enabled}
              >
                Put My Record In The Lab
              </button>
            </div>
          )}
        />
      </section>

      <section
        className="hitlab-shell hitlab-radio-landing-card hitlab-live-proof-card"
        aria-label="Joe Galaxy Radio Rotation"
      >
        <div className="hitlab-radio-landing-copy">
          <p className="hitlab-kicker">JG RADIO ROTATION</p>
          {radioLive ? (
            <>
              <h2>Records are already in rotation.</h2>
              <p>
                Joe Galaxy Radio Rotation is live inside JG Universe. HitLab is
                the gate for artists with one original record strong enough to
                earn the next move.
              </p>
              <p>
                Every valid submission gets a JG HitLab Listen Result. If the
                record earns it, it can become a candidate for the Radio
                Rotation path.
              </p>
              <p className="hitlab-radio-proof-line">
                Not empty exposure. Real records. Real listeners. Real signals.
              </p>
              <p className="hitlab-hero-trust">
                Placement cannot be bought. The record has to prove it.
              </p>
            </>
          ) : (
            <>
              <h2>Joe Galaxy Radio Rotation is preparing for launch.</h2>
              <p>
                Rights-clean records will appear here as the rotation opens.
              </p>
            </>
          )}
        </div>
        <div className="hitlab-radio-landing-panel">
          {radioLive && featuredPreviewItem ? (
            <div className="hitlab-radio-landing-now">
              {previewItemIsTrack ? (
                <img
                  src={featuredPreviewItem.coverArtUrl}
                  alt={`Cover art for ${featuredPreviewItem.songTitle} by ${featuredPreviewItem.artistName}`}
                  width="132"
                  height="132"
                  loading="lazy"
                  decoding="async"
                />
              ) : null}
              {previewItemIsStationProgramming ? (
                <div
                  className={[
                    'hitlab-radio-landing-bumper-visual',
                    previewItemIsDrop ? 'hitlab-radio-drop-visual' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-testid="hitlab-landing-radio-bumper-visual"
                  aria-label={
                    previewItemIsDrop
                      ? 'Listener drop preview visual'
                      : 'Station bumper preview visual'
                  }
                >
                  <span>JG</span>
                  <strong>{previewItemIsDrop ? 'VOICE DROP' : 'ON AIR'}</strong>
                </div>
              ) : null}
              <div className="hitlab-radio-landing-now-copy">
                <span>
                  {previewItemIsDrop
                    ? 'Listener drop'
                    : previewItemIsBumper
                      ? 'Station sweep'
                      : 'Now in rotation'}
                </span>
                <strong>{previewTitle}</strong>
                <small>{previewSubtitle}</small>
              </div>
              <div
                className={`hitlab-radio-landing-bars ${
                  previewPlaying ? 'is-playing' : ''
                }`}
                aria-hidden="true"
              >
                <span />
                <span />
                <span />
                <span />
              </div>
            </div>
          ) : (
            <div className="hitlab-radio-landing-orbit" aria-hidden="true">
              <span />
              <strong>JG</strong>
            </div>
          )}
          {radioLive && featuredPreviewItem ? (
            <audio
              ref={previewAudioRef}
              src={featuredPreviewItem.audioUrl}
              preload="metadata"
              onLoadedMetadata={handlePreviewLoadedMetadata}
              onTimeUpdate={handlePreviewTimeUpdate}
              onEnded={handlePreviewEnded}
              onError={handlePreviewError}
              aria-label="HitLab Radio preview audio"
            />
          ) : null}
          <div className="hitlab-radio-landing-status">
            <span>{radioLive ? 'Live rotation' : 'Preparing'}</span>
            <strong>Radio Rotation</strong>
          </div>
          {radioLive && featuredPreviewItem ? (
            <>
              <div className="hitlab-radio-landing-progress-row">
                <span>{formatPreviewTime(previewElapsed)}</span>
                <div
                  className="hitlab-radio-landing-progress"
                  role="progressbar"
                  aria-label="HitLab Radio preview progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={Math.round(previewProgress)}
                >
                  <span style={{ width: `${previewProgress}%` }} />
                </div>
                <span>{formatPreviewTime(previewDuration)}</span>
              </div>
              {previewError ? (
                <p className="hitlab-radio-landing-error" role="alert">
                  {previewError}
                </p>
              ) : null}
              <div className="hitlab-radio-landing-actions">
                <button
                  type="button"
                  className="hitlab-primary-btn"
                  onClick={toggleRadioPreview}
                  aria-label={
                    previewPlaying
                      ? 'Pause HitLab Radio preview'
                      : 'Play HitLab Radio preview'
                  }
                >
                  {previewLoading
                    ? 'Loading'
                    : previewPlaying
                      ? 'Pause Preview'
                      : 'Play Preview'}
                </button>
                <button
                  type="button"
                  className="hitlab-secondary-btn"
                  onClick={openFullRadio}
                >
                  Open Full Radio
                </button>
              </div>
            </>
          ) : null}
        </div>
      </section>

      <section
        id="hitlab-qualifies"
        className="hitlab-shell hitlab-proof-grid"
        aria-label="What qualifies for JG HitLab"
      >
        <article className="hitlab-wide-card">
          <span>THE FILTER</span>
          <h2>We are looking for records that move people.</h2>
          <p>
            Songs with feeling. Songs with replay value. Songs that deserve
            more than getting buried in a link, a DM, or an algorithm.
          </p>
          <p>
            When your song enters HitLab, it begins the path into the JG Universe:
            JG Profile. HitLab intake. Rights review. JG Radio consideration.
            Listener reaction. Feedback. Support signal. Earned next moves.
          </p>
          <p>
            You cannot buy placement. You cannot buy a hit. The record has to
            prove it.
          </p>
        </article>
        <article>
          <span>ONE SHOT</span>
          <h3>One artist profile</h3>
          <p>One original record, one clean submission path, no catalog dump.</p>
        </article>
        <article>
          <span>BOUNDARY</span>
          <h3>No shortcuts</h3>
          <p>
            No covers, uncleared samples, unclear beat rights, or unresolved
            producer approvals.
          </p>
        </article>
      </section>

      <section
        className="hitlab-shell hitlab-performance-proof"
        aria-label="Joe Galaxy real performance history"
      >
        <div className="hitlab-performance-proof-copy">
          <p className="hitlab-kicker">REAL PERFORMANCE HISTORY</p>
          <h2>Real stages. Real rooms. Serious curation.</h2>
          <p>
            JG HitLab is not a random upload box. It is connected to real
            performance history, real audience rooms, and serious review.
          </p>
        </div>
        <article className="hitlab-proof-media-card">
          <div className="hitlab-proof-media-frame">
            <img
              src={HITLAB_MEDIA.backstage.src}
              srcSet={HITLAB_MEDIA.backstage.srcSet}
              sizes={HITLAB_MEDIA.backstage.sizes}
              alt={HITLAB_MEDIA.backstage.alt}
              width="520"
              height="420"
              loading="lazy"
              decoding="async"
            />
          </div>
          <span>REAL PERFORMANCE HISTORY</span>
          <h3>Backstage at SunFest</h3>
          <p>Stage-tested proof behind the review lane.</p>
        </article>
      </section>

      <section
        className="hitlab-shell hitlab-midpage-cta"
        aria-label="Start your JG HitLab artist profile"
      >
        <div>
          <p className="hitlab-kicker">READY WHEN YOU ARE</p>
          <h2>Ready to bring the song you'd bet on?</h2>
          <p>
            Create your JG HitLab artist profile and put one original record
            into the intake.
          </p>
        </div>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={startIntake}
          disabled={!enabled}
        >
          Put My Record In The Lab
        </button>
      </section>

      <section
        className="hitlab-shell hitlab-gate"
        aria-label="JG HitLab Listen Result promise"
      >
        <div>
          <p className="hitlab-kicker">THE RESULT</p>
          <h2>Every valid submission gets a JG HitLab Listen Result.</h2>
          <p>
            After your record is processed and heard, you get a clear result:
            earned next move, not yet, or hold for second listen.
          </p>
          <p className="hitlab-hero-trust">
            You are not throwing a song into the dark. The record has to prove
            it, and you will know where it stands.
          </p>
        </div>
        <ul>
          <li>Valid submission</li>
          <li>Processed and heard</li>
          <li>Clear result</li>
          <li>Placement cannot be bought</li>
        </ul>
      </section>

      <section className="hitlab-band">
        <div className="hitlab-shell hitlab-opportunity">
          <div>
            <p className="hitlab-kicker">WHY ONE RECORD?</p>
            <h2>Why One Record?</h2>
            <p>
              Because curation rewards focus. If you can't choose the record
              you would bet on, neither can the listener.
            </p>
          </div>
          <div className="hitlab-path-list">
            <article>
              <strong>One artist profile</strong>
              <span>One clean identity behind the record.</span>
            </article>
            <article>
              <strong>One original record</strong>
              <span>Your strongest song gets the clearest listen.</span>
            </article>
            <article>
              <strong>No catalog dumps</strong>
              <span>The intake is built for focus, not volume.</span>
            </article>
            <article>
              <strong>Placement cannot be bought</strong>
              <span>The song has to earn its next step.</span>
            </article>
          </div>
        </div>
      </section>

      <section className="hitlab-shell hitlab-gate">
        <div>
          <p className="hitlab-kicker">THIS IS NOT</p>
          <h2>Not Another Fake Music Opportunity</h2>
          <p>
            No label bait. No bought placement. No playlist promise. One
            focused door for one serious original record.
          </p>
        </div>
        <ul>
          <li>Not bought placement</li>
          <li>Not a record-deal promise</li>
          <li>Not a playlist guarantee</li>
          <li>Not a place to dump your whole catalog</li>
        </ul>
      </section>

      <section className="hitlab-shell hitlab-gate hitlab-paths-section">
        <div>
          <p className="hitlab-kicker">NEXT STEPS</p>
          <h2>Strong Records Move Forward</h2>
          <p>
            The intake starts with one record. The next step is earned by the
            song, the rights, and Joe Galaxy's curator decision.
          </p>
          <p className="hitlab-hero-trust">
            Not every record moves forward. Placement cannot be bought.
          </p>
        </div>
        <div className="hitlab-path-list">
          <article>
            <strong>JG HitLab</strong>
            <span>
              The intake lane for serious records: artist profile, rights check,
              curator review, and next-step decision.
            </span>
          </article>
          <article>
            <strong>Joe Galaxy Hit Radar</strong>
            <span>
              Joe Galaxy's shortlist for records with standout hook, identity,
              replay value, and market energy.
            </span>
          </article>
          <article>
            <strong>Joe Galaxy Radio Rotation</strong>
            <span>Selected rights-clean records enter Joe Galaxy Radio Rotation.</span>
          </article>
          <article>
            <strong>LocalOps Street Promo</strong>
            <span>
              Region-ready records become candidates for proof-backed local
              street campaigns powered by JG LocalOps helpers.
            </span>
          </article>
        </div>
      </section>

      <section className="hitlab-shell hitlab-final-cta">
        <div>
          <p className="hitlab-kicker">ONE RECORD</p>
          <h2>Bring the song you would bet on.</h2>
          <p>Start your JG artist intake.</p>
        </div>
        <button
          type="button"
          className="hitlab-primary-btn"
          onClick={startIntake}
          disabled={!enabled}
        >
          {ctaLabel}
        </button>
        <div
          className="hitlab-instagram-dm"
          role="region"
          aria-label="Joe Galaxy Instagram DM handoff"
        >
          <p className="hitlab-kicker">QUESTIONS</p>
          <div className="hitlab-instagram-dm-copy">
            <strong>Questions?</strong>
            <span>Follow me on Instagram and hit my DM.</span>
          </div>
          <a
            className="hitlab-instagram-dm-link"
            href={HITLAB_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Instagram
          </a>
        </div>
      </section>
    </main>
  );
}
